import { Button, Input, Select, TableProps, Tooltip } from "antd";
import { useState } from "react";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import RefreshIcon from "../../../assets/icons/RefreshIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import { useGetProtalAllSubDepartmentQuery } from "../../../features/portal/settings/settingsApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import {
  GetProtalAllSubDepartmentDataType,
  GetProtalAllSubDepartmentParamsType,
} from "../../../types/portal/settings";
import { countParPageSerialNumber } from "../../../utils/number";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";
import SubDepartmentAddModal from "./SubDepartmentAddModal";
import SubDepartmentStatusUpdateModal from "./SubDepartmentStatusUpdateModal";

const leaveSettingsTabList = [
  {
    label: "Department",
    link: `/admin/settings`,
  },
  {
    label: "Sub Department",
    link: `/admin/settings/sub-department`,
  },
];

export default function SubDepartmentList() {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetProtalAllSubDepartmentParamsType>();
  const { open, handleOpen, handleClose } = useModal<string, any>();
  const {
    open: productActivityOpen,
    handleOpen: handleProductActivityOpen,
    handleClose: handleProductActivityClose,
  } = useModal<string, any>();

  const { data, isError, error, isLoading } = useGetProtalAllSubDepartmentQuery(
    {
      params: {
        status:
          filter.status === 1 ? true : filter.status === 0 ? false : undefined,
        skip: undefined,
        page,
        search: keyword,
      },
    }
  );
  useError(isError, error, "Get all sub department data failed");

  const columns: TableProps<GetProtalAllSubDepartmentDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Sub Department Name",
      dataIndex: "name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: "Parent Department",
      dataIndex: "department",
      render: (val) => (val?.name ? val?.name : "N/A"),
    },
    // {
    //   title: "Parent Company",
    //   dataIndex: "parent_company",
    //   render: (val: string) => (val ? val : "N/A"),
    // },
    // {
    //   title: "Head of Department",
    //   dataIndex: "head_of_department",
    //   render: (val: string) => (val ? val : "N/A"),
    // },
    {
      title: "No. of Employee",
      dataIndex: "employee_count",
      align: "center",
      render: (val) => (val ? val : "0"),
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      align: "center",
      render: (val) => (val ? formatDate(val) : "N/A"),
    },

    {
      title: <div className="whitespace-nowrap">Status</div>,
      align: "center",
      render: (val: { status: boolean }) => {
        const isActive = Boolean(val?.status); // true or false
        const displayStatus = isActive ? "active" : "inactive";

        const statusClasses: Record<string, string> = {
          active: "bg-green-100 text-green-700",
          inactive: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[displayStatus]}`}
          >
            {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      align: "center",
      render: (record: any) => (
        <div className="flex justify-center items-center gap-2">
          <Tooltip title="Edit Department">
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() => {
                handleOpen({
                  type: "ADD",
                  data: record,
                });
              }}
            />
          </Tooltip>{" "}
          <Tooltip title="View Department">
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
              onClick={() => {
                handleOpen({
                  type: "ADD",
                  data: record,
                });
                setIsViewOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit Status">
            <Button
              className="action-button"
              size="small"
              icon={<RefreshIcon />}
              onClick={() =>
                handleProductActivityOpen({
                  type: "PRODUCT_ACTIVITY",
                  data: record,
                })
              }
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleCloseView = () => {
    setIsViewOpen(false);
    handleClose();
  };

  return (
    <>
      {open?.type === "ADD" && (
        <SubDepartmentAddModal
          open={open?.open}
          data={open?.data}
          handleClose={handleCloseView}
          isViewOpen={isViewOpen}
        />
      )}

      {productActivityOpen?.type === "PRODUCT_ACTIVITY" && (
        <SubDepartmentStatusUpdateModal
          title=""
          subTitle=""
          buttonName="Send"
          open={productActivityOpen?.open}
          data={productActivityOpen?.data}
          handleClose={handleProductActivityClose}
          action
          buttonBgDeep
          loading={isLoading}
        />
      )}

      <SidebarLayout title="Sub Department" tabList={leaveSettingsTabList}>
        {isLoading ? (
          <Loader />
        ) : (
          <CustomTable
            loading={false}
            limit={limit}
            page={page}
            columns={columns}
            total={data?.data?.total || 0}
            dataSource={addKeyInArray(data?.data?.data)}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            filterSection={
              <>
                <Input
                  suffix={<SearchIcon />}
                  placeholder="Search by sub department name"
                  style={{ width: 250 }}
                  allowClear
                  onChange={handleSearch}
                />

                <Select
                  allowClear
                  showSearch
                  placeholder="Status"
                  filterOption={selectSearchOption}
                  options={[
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                  ]}
                  onChange={(value) => {
                    handleFilter(
                      "status",
                      value === "active"
                        ? 1
                        : value === "inactive"
                        ? 0
                        : undefined
                    );
                  }}
                  className="min-w-[90px]"
                  suffixIcon={<SelectDownIcon />}
                  rootClassName="icon-gray"
                />

                <Button
                  type="primary"
                  className="middle-button"
                  icon={<AddIcon />}
                  onClick={() =>
                    handleOpen({
                      type: "ADD",
                    })
                  }
                >
                  Add New Sub Department
                </Button>
              </>
            }
          />
        )}
      </SidebarLayout>
    </>
  );
}
